import React, { useState, useRef, useCallback } from 'react';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useCesium } from '../../../../context/CesiumContext';
import LayersPanel from '../../../SettingLayer/components/LayersPanel';
import OrbitPanel from './OrbitPanel';
import styles from './RightTools.module.scss';
import CustomToolTip from '../../../../../../CommonComponents/CustomToolTip';

// ─── Icons ────────────────────────────────────────────────────────────────────
const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);
const ZoomInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const ZoomOutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M5 12h14" />
  </svg>
);
const ResetViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
  </svg>
);
const LayersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="m12 2 10 6-10 6L2 8z" />
    <path d="m2 14 10 6 10-6" />
  </svg>
);
const SplitViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <rect x="2" y="3" width="20" height="18" rx="2" />
    <line x1="12" y1="3" x2="12" y2="21" />
  </svg>
);
const OrbitIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <ellipse cx="12" cy="12" rx="10" ry="4" />
    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <line x1="4" y1="6" x2="10" y2="6" />
    <line x1="14" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="6" y2="12" />
    <line x1="10" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="14" y2="18" />
    <line x1="18" y1="18" x2="20" y2="18" />
    <circle cx="12" cy="6" r="1.6" fill="currentColor" />
    <circle cx="8" cy="12" r="1.6" fill="currentColor" />
    <circle cx="16" cy="18" r="1.6" fill="currentColor" />
  </svg>
);

const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
const RightTools: React.FC = () => {
  const { drawerMode, viewMode, setViewMode, timelineCollapsed, timelineHeight } = useMDCockpit();
  const cesium = useCesium();

  const [collapsed, setCollapsed] = useState(true);
  const [layersOpen, setLayersOpen] = useState(false);
  const [orbitOpen, setOrbitOpen] = useState(false);
  const [lightingOn, setLightingOn] = useState(true);
  const [gridOn, setGridOn] = useState(() => cesium.isGridVisible?.() ?? false);
  const layersBtnRef = useRef<HTMLButtonElement>(null);
  const orbitBtnRef = useRef<HTMLButtonElement>(null);
  const lastSingleModeRef = useRef<'2D' | '3D'>('3D');
  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (collapsed) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setCollapsed(true);
      setLayersOpen(false);
      setOrbitOpen(false);
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [collapsed]);

  if (viewMode === '2D' || viewMode === '3D') {
    lastSingleModeRef.current = viewMode;
  }

  const handleGrid = useCallback(() => {
    const next = !gridOn;
    setGridOn(next);
    cesium.setGridVisible(next);
  }, [gridOn, cesium]);

  const handleLighting = useCallback(() => {
    setLightingOn(v => !v);
    cesium.toggleLighting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLayers = useCallback(() => {
    setLayersOpen(v => !v);
    setOrbitOpen(false);
  }, []);

  const closeLayers = useCallback(() => {
    setLayersOpen(false);
    setOrbitOpen(false);
  }, []);

  if (drawerMode === 'half' || drawerMode === 'expanded') return null;

  const stackClass = [styles.rtools, collapsed ? styles.collapsed : ''].filter(Boolean).join(' ');
  const timelineOffset = timelineCollapsed ? 82 : timelineHeight + 52;

  return (
    <>
      <div ref={containerRef} className={stackClass} style={{ bottom: timelineOffset }}>
        {/* ── Action buttons (hidden when collapsed) ── */}
        <CustomToolTip title="Eclipse" placement="left">
          <button
            className={`${styles.rtool} ${styles.rtoolAction} ${lightingOn ? styles.active : ''}`}
            type="button"
            onClick={handleLighting}
          >
            <SunIcon />
          </button>
        </CustomToolTip>

        <CustomToolTip title="Zoom in" placement="left">
          <button
            className={`${styles.rtool} ${styles.rtoolAction}`}
            type="button"
            onClick={cesium.zoomIn}
          >
            <ZoomInIcon />
          </button>
        </CustomToolTip>

        <CustomToolTip title="Zoom out" placement="left">
          <button
            className={`${styles.rtool} ${styles.rtoolAction}`}
            type="button"
            onClick={cesium.zoomOut}
          >
            <ZoomOutIcon />
          </button>
        </CustomToolTip>

        <CustomToolTip title="Reset view" placement="left">
          <button
            className={`${styles.rtool} ${styles.rtoolAction}`}
            type="button"
            onClick={cesium.resetZoom}
          >
            <ResetViewIcon />
          </button>
        </CustomToolTip>

        <CustomToolTip title="Split View (2D | 3D)" placement="left">
          <button
            className={`${styles.rtool} ${styles.rtoolAction} ${viewMode === 'split' ? styles.active : ''}`}
            type="button"
            onClick={() => {
              if (viewMode === 'split') {
                const prev = lastSingleModeRef.current;
                setViewMode(prev);
                cesium.setSceneMode(prev === '2D' ? 'SCENE2D' : 'SCENE3D');
                cesium.destroySecondaryViewer();
              } else {
                cesium.setSceneMode('SCENE3D');
                setViewMode('split');
              }
            }}
          >
            <SplitViewIcon />
          </button>
        </CustomToolTip>

        <CustomToolTip title="Layers" placement="left">
          <button
            ref={layersBtnRef}
            className={`${styles.rtool} ${styles.rtoolAction} ${layersOpen ? styles.active : ''}`}
            type="button"
            onClick={handleLayers}
          >
            <LayersIcon />
          </button>
        </CustomToolTip>

        <CustomToolTip title="Orbit lead / trail" placement="left">
          <button
            ref={orbitBtnRef}
            className={`${styles.rtool} ${styles.rtoolAction} ${orbitOpen ? styles.active : ''}`}
            type="button"
            onClick={() => { setOrbitOpen(v => !v); setLayersOpen(false); }}
          >
            <OrbitIcon />
          </button>
        </CustomToolTip>

        <CustomToolTip title="Toggle Grid" placement="left">
          <button
            className={`${styles.rtool} ${styles.rtoolAction} ${gridOn ? styles.active : ''}`}
            type="button"
            onClick={handleGrid}
          >
            <GridIcon />
          </button>
        </CustomToolTip>

        {/* ── Settings toggle (always visible) ── */}
        <CustomToolTip title="View settings" placement="left">
          <button
            className={`${styles.rtool} ${collapsed ? '' : styles.settingsOpen}`}
            type="button"
            onClick={() => { setCollapsed(v => !v); if (!collapsed) setLayersOpen(false); }}
          >
            <SettingsIcon />
          </button>
        </CustomToolTip>
      </div>

      {/* ── Layers panel — anchored to the layers button ── */}
      {layersOpen && (
        <LayersPanel btnRef={layersBtnRef} onClose={closeLayers} />
      )}

      {/* ── Orbit lead/trail panel ── */}
      {orbitOpen && (
        <OrbitPanel btnRef={orbitBtnRef} onClose={() => setOrbitOpen(false)} />
      )}
    </>
  );
};

export default RightTools;
